// Create / edit teacher (SPEC §7.5): names, contacts, note, courses (checkboxes), weekly unavailability.

import type { ApiResult } from "../../api/client";
import type { TeacherFields } from "../../api/teachers";
import type { Course, Teacher } from "../../api/types";
import { t } from "../../i18n/i18n";
import { createButton, createElement } from "../../ui/dom";
import { createErrorBox, showProblem } from "../../ui/feedback";
import { createCheckbox, createInputField, createTextAreaField, showFieldErrors, type Field } from "../../ui/forms";
import { createWeeklyEditor, type WeeklyEditor } from "../../ui/weeklyEditor";

export interface TeacherFormOptions {
  teacher: Teacher | null;
  courses: Course[];
  save: (fields: TeacherFields) => Promise<ApiResult<Teacher>>;
  onSaved: (teacher: Teacher) => void;
}

export function buildTeacherForm(options: TeacherFormOptions): HTMLElement {
  const teacher: Teacher | null = options.teacher;
  const fields: Record<string, Field<HTMLInputElement>> = {
    firstName: createInputField(t("teachers.firstName"), "text", teacher?.firstName ?? ""),
    lastName: createInputField(t("teachers.lastName"), "text", teacher?.lastName ?? ""),
    phone: createInputField(t("teachers.phone"), "tel", teacher?.phone ?? ""),
    email: createInputField(t("teachers.email"), "email", teacher?.email ?? ""),
    telegram: createInputField(t("teachers.telegram"), "text", teacher?.telegram ?? ""),
  };
  const note: Field<HTMLTextAreaElement> = createTextAreaField(t("app.note"), teacher?.note ?? "");
  const courseBoxes: Map<string, Field<HTMLInputElement>> = buildCourseBoxes(options.courses, teacher);
  const weekly: WeeklyEditor = createWeeklyEditor(teacher?.weeklyUnavailable ?? []);
  const errorBox: HTMLDivElement = createErrorBox();
  const save: HTMLButtonElement = createButton(t("app.save"), async (): Promise<void> => {
    const result: ApiResult<Teacher> = await options.save(readFields(fields, note, courseBoxes, weekly));
    showProblem(errorBox, result.ok ? null : result.problem);
    showFieldErrors(result.ok ? null : result.problem, { ...fields, note });
    if (result.ok) {
      options.onSaved(result.data);
    }
  }, "button-primary");
  const grid: HTMLDivElement = createElement("div", "form-grid");
  grid.append(...Object.values(fields).map((field: Field<HTMLInputElement>): HTMLElement => field.wrapper));
  const courses: HTMLFieldSetElement = createElement("fieldset");
  courses.append(createElement("legend", "", t("teachers.courses")),
    ...[...courseBoxes.values()].map((box: Field<HTMLInputElement>): HTMLElement => box.wrapper));
  const card: HTMLDivElement = createElement("div", "card");
  card.append(grid, note.wrapper, courses, weekly.element, errorBox, save);
  return card;
}

function buildCourseBoxes(courses: Course[], teacher: Teacher | null): Map<string, Field<HTMLInputElement>> {
  const boxes: Map<string, Field<HTMLInputElement>> = new Map<string, Field<HTMLInputElement>>();
  for (const course of courses) {
    const isLinked: boolean = teacher?.courseIds.includes(course.id) ?? false;
    if (course.archived === false || isLinked) {
      boxes.set(course.id, createCheckbox(course.name, isLinked));
    }
  }

  return boxes;
}

function readFields(fields: Record<string, Field<HTMLInputElement>>, note: Field<HTMLTextAreaElement>,
  courseBoxes: Map<string, Field<HTMLInputElement>>, weekly: WeeklyEditor): TeacherFields {
  const value = (name: string): string => fields[name]?.input.value ?? "";
  const courseIds: string[] = [...courseBoxes.entries()]
    .filter(([, box]: [string, Field<HTMLInputElement>]): boolean => box.input.checked)
    .map(([courseId]: [string, Field<HTMLInputElement>]): string => courseId);
  return {
    firstName: value("firstName"), lastName: value("lastName"), phone: value("phone"), email: value("email"),
    telegram: value("telegram"), note: note.input.value, courseIds, weeklyUnavailable: weekly.getEntries(),
  };
}
